import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Youtube, Facebook, Instagram, Music2, Plus, Link2, Check, AlertTriangle, Loader2, Trash2, Send, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { getSocialPlatforms, getSocialAccounts, validateSocialUrl, startSocialOAuth, disconnectSocial, getAutopost, setAutopost, errText } from "../lib/api";
import { devError } from "../lib/log";

const ICONS = { youtube: Youtube, facebook: Facebook, instagram: Instagram, tiktok: Music2 };

export default function Connections() {
  const [platforms, setPlatforms] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [prefs, setPrefs] = useState({ enabled: false, platforms: [], delay_minutes: 0 });
  const [url, setUrl] = useState("");
  const [detected, setDetected] = useState(null);
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const [pf, acc, ap] = await Promise.all([getSocialPlatforms(), getSocialAccounts(), getAutopost()]);
      setPlatforms(pf); setAccounts(acc); setPrefs(ap);
    } catch (e) { devError("Connections load failed:", e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get("social_connected")) { toast.success(`${params.get("social_connected")} connected!`); load(); setParams({}); }
    if (params.get("social_error") === "no_channel") { toast.error("That Google account has no YouTube channel. Create one at youtube.com, then connect again.", { duration: 8000 }); setParams({}); }
    else if (params.get("social_error")) { toast.error("Could not connect account. Please try again."); setParams({}); }
  }, [params, setParams, load]);

  const accountFor = (p) => accounts.find((a) => a.platform === p);

  const connect = async (platform) => {
    setBusy(platform);
    try {
      const { authorize_url } = await startSocialOAuth(platform);
      window.location.href = authorize_url;
    } catch (e) { toast.error(errText(e)); setBusy(""); }
  };

  const disconnect = async (platform) => {
    try { await disconnectSocial(platform); toast.success("Disconnected"); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  const doValidate = async () => {
    setDetected(null);
    try { const r = await validateSocialUrl(url); setDetected(r); }
    catch (e) { toast.error(errText(e)); }
  };

  const togglePlatformPref = (p) => {
    setPrefs((pr) => ({ ...pr, platforms: pr.platforms.includes(p) ? pr.platforms.filter((x) => x !== p) : [...pr.platforms, p] }));
  };

  const savePrefs = async () => {
    try { const saved = await setAutopost(prefs); setPrefs(saved); toast.success("Auto-posting settings saved"); }
    catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12" data-testid="connections-page">
      <p className="eyebrow">Social</p>
      <h1 className="font-display font-extrabold text-3xl sm:text-4xl mt-2">Social media connections</h1>
      <p className="text-slate-400 mt-2 max-w-2xl">Connect your accounts to auto-publish finished videos. Each platform uses its official, secure sign-in — we never see your password, and tokens are encrypted.</p>

      {/* URL identify helper */}
      <div className="mt-8 rounded-2xl border border-white/10 bg-[#11141E] p-5">
        <label className="text-sm font-medium text-slate-300 flex items-center gap-2"><Link2 className="h-4 w-4 text-[#FF6A5E]" /> Paste a profile URL to find your platform</label>
        <div className="mt-3 flex gap-2">
          <Input data-testid="url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://youtube.com/@yourchannel" className="bg-[#0B0C10] border-white/10" />
          <Button data-testid="url-validate-btn" onClick={doValidate} variant="outline" className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-white/5">Identify</Button>
        </div>
        {detected && (
          <p className="mt-3 text-sm text-slate-300" data-testid="url-detected">
            Detected <strong className="text-white">{detected.platform_name}</strong>.{" "}
            {detected.configured
              ? <button className="text-[#FF6A5E] underline" onClick={() => connect(detected.platform)}>Connect it now →</button>
              : <span className="text-amber-400">Setup pending — this platform isn't available to connect yet.</span>}
          </p>
        )}
        <p className="mt-2 text-xs text-slate-500">Note: a profile URL alone doesn't authorize posting — you'll complete the platform's official sign-in.</p>
      </div>

      {/* Platform cards */}
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        {platforms.map((p) => {
          const Icon = ICONS[p.id] || Send;
          const acc = accountFor(p.id);
          const reauth = acc?.status === "reauth_required";
          return (
            <div key={p.id} data-testid={`platform-card-${p.id}`} className="rounded-2xl border border-white/10 bg-[#11141E] p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: `${p.color}22`, color: p.color }}><Icon className="h-5 w-5" /></span>
                  <div>
                    <p className="font-semibold text-white">{p.name}</p>
                    {acc ? (
                      <p className="text-xs text-slate-400">{acc.account_name}</p>
                    ) : p.configured ? (
                      <p className="text-xs text-slate-500">Not connected</p>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-400"><Clock className="h-3 w-3" /> Setup pending</span>
                    )}
                  </div>
                </div>
                {acc && !reauth && <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><Check className="h-3.5 w-3.5" /> Connected</span>}
                {reauth && <span className="inline-flex items-center gap-1 text-xs text-red-400"><AlertTriangle className="h-3.5 w-3.5" /> Reconnect</span>}
              </div>
              <div className="mt-4 flex gap-2">
                {!acc && (
                  <Button data-testid={`connect-${p.id}`} disabled={!p.configured || busy === p.id} onClick={() => connect(p.id)}
                    className="rounded-full bg-[#FF453A] hover:bg-[#FF5D54] text-white text-sm px-4 disabled:opacity-40">
                    {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> {p.configured ? "Connect" : "Setup pending"}</>}
                  </Button>
                )}
                {acc && (
                  <>
                    {reauth && <Button data-testid={`reconnect-${p.id}`} disabled={busy === p.id} onClick={() => connect(p.id)} className="rounded-full bg-[#FF453A] hover:bg-[#FF5D54] text-white text-sm px-4">Reconnect</Button>}
                    {acc.profile_url && <a href={acc.profile_url} target="_blank" rel="noreferrer" className="text-xs text-slate-400 self-center underline">View profile</a>}
                    <Button data-testid={`disconnect-${p.id}`} onClick={() => disconnect(p.id)} variant="outline" className="rounded-full border-white/15 bg-transparent text-slate-300 hover:bg-white/5 text-sm px-4"><Trash2 className="h-3.5 w-3.5 mr-1" /> Disconnect</Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Auto-post settings */}
      <div className="mt-8 rounded-2xl border border-white/10 bg-[#11141E] p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-white flex items-center gap-2"><Send className="h-4 w-4 text-[#FF6A5E]" /> Automatic publishing</p>
            <p className="text-xs text-slate-400 mt-1">When a video finishes, auto-publish it to your selected connected platforms. Publishing uses no extra video credits.</p>
          </div>
          <Switch data-testid="autopost-toggle" checked={prefs.enabled} onCheckedChange={(v) => setPrefs((p) => ({ ...p, enabled: v }))} />
        </div>
        {prefs.enabled && (
          <div className="mt-4 space-y-4">
            <div>
              <p className="text-xs text-slate-400 mb-2">Publish to:</p>
              <div className="flex flex-wrap gap-2">
                {platforms.filter((p) => accountFor(p.id) && accountFor(p.id).status === "connected").map((p) => (
                  <button key={p.id} data-testid={`autopost-plat-${p.id}`} onClick={() => togglePlatformPref(p.id)}
                    className={`rounded-full px-3 py-1.5 text-sm border ${prefs.platforms.includes(p.id) ? "border-[#FF453A] bg-[#FF453A]/15 text-white" : "border-white/15 text-slate-300"}`}>
                    {p.name}
                  </button>
                ))}
                {platforms.filter((p) => accountFor(p.id) && accountFor(p.id).status === "connected").length === 0 && (
                  <span className="text-xs text-slate-500">Connect an account above to enable auto-posting.</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs text-slate-400">Delay before posting (minutes):</label>
              <Input data-testid="autopost-delay" type="number" min="0" value={prefs.delay_minutes} onChange={(e) => setPrefs((p) => ({ ...p, delay_minutes: Math.max(0, parseInt(e.target.value || "0")) }))} className="w-24 bg-[#0B0C10] border-white/10 h-9" />
              <span className="text-xs text-slate-500">0 = publish immediately</span>
            </div>
          </div>
        )}
        <Button data-testid="autopost-save" onClick={savePrefs} className="mt-4 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm px-5">Save settings</Button>
      </div>
    </div>
  );
}
